/**
 * Image optimization utilities for Supabase Storage
 *
 * Supabase supports image transformations via URL parameters:
 * - format: webp, jpeg, png (default: original format)
 * - width: resize width in pixels
 * - height: resize height in pixels
 * - quality: 1-100 (default: 80)
 * - resize: cover, contain, fill
 */

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  format?: 'webp' | 'jpeg' | 'png';
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Optimizes a Supabase storage URL with WebP format and optional transformations
 * Falls back to original URL if not a Supabase storage URL
 */
export function optimizeSupabaseImage(
  url: string | null | undefined,
  options: ImageTransformOptions = {}
): string {
  if (!url) return '';

  // Only transform Supabase storage URLs
  if (!url.includes('supabase.co/storage/v1/object/public')) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams();

    // Default to WebP for better compression
    const format = options.format || 'webp';
    params.set('format', format);

    // Default quality to 80 for good balance between size and quality
    const quality = options.quality || 80;
    params.set('quality', quality.toString());

    // Add width if specified
    if (options.width) {
      params.set('width', options.width.toString());
    }

    // Add height if specified
    if (options.height) {
      params.set('height', options.height.toString());
    }

    // Add resize mode if specified
    if (options.resize) {
      params.set('resize', options.resize);
    }

    // Construct optimized URL
    const optimizedUrl = `${urlObj.origin}${urlObj.pathname}?${params.toString()}`;
    return optimizedUrl;
  } catch (error) {
    console.warn('Failed to optimize image URL:', error);
    return url;
  }
}

/**
 * Generate responsive srcSet for Supabase images
 * Returns srcSet string with multiple widths for responsive images
 */
export function generateSupabaseImageSrcSet(
  url: string | null | undefined,
  widths: number[] = [400, 800, 1200, 1600]
): string {
  if (!url) return '';

  const srcSetParts = widths.map((width) => {
    const optimizedUrl = optimizeSupabaseImage(url, { width, format: 'webp' });
    return `${optimizedUrl} ${width}w`;
  });

  return srcSetParts.join(', ');
}

/**
 * Presets for common image sizes
 */
export const IMAGE_PRESETS = {
  thumbnail: { width: 200, height: 200, resize: 'cover' as const },
  avatar: { width: 150, height: 150, resize: 'cover' as const },
  card: { width: 400, height: 300, resize: 'cover' as const },
  hero: { width: 1200, quality: 85 },
  fullWidth: { width: 1600, quality: 85 },
} as const;
